import type { ZudokuPlugin } from "zudoku";
import tenantUtils from "../../src/utils/tenant";

export interface TenantNavigationOptions {
  tenantClaim?: string;
}

export function createTenantNavigationPlugin(options?: TenantNavigationOptions): ZudokuPlugin {
  const { tenantClaim = "urn:zitadel:iam:user:resourceowner:id" } = options || {};

  return {
    name: "zudoku-plugin-tenant-navigation",
    // The nav plugin lifecycle — return navigation or use provided `getNavigation` hook
    getNavigation: async (first: any, context?: any) => {
      // `first` can be `site` or `on` (a getter function) depending on plugin API
      let site = first;
      let ctx = context;
      if (typeof first === "function") {
        try {
          site = first();
        } catch (err) {
          site = first?.(null);
        }
      }
      if (!ctx) ctx = context;
      try {
        // Synchronously read token claim if `getAccessToken` returns string
        const token = context?.authentication?.getAccessToken?.();
        let userTenants: string[] = [];
        if (token && typeof token === "string") {
          const payload = tenantUtils.decodeJwtPayload(token);
          const claim = payload?.[tenantClaim] ?? payload?.org ?? payload?.project ?? payload?.sub;
          userTenants = Array.isArray(claim) ? claim : claim ? [String(claim)] : [];
        }

        function filterNav(navItems: any[]): any[] {
          if (!navItems || navItems.length === 0) return navItems;
          const result: any[] = [];
          for (const item of navItems) {
            const allowed = item.allowedTenants as string[] | undefined;
            if (allowed && !tenantUtils.isTenantAllowed(allowed, userTenants)) {
              // Debug log which nav item is being skipped due to tenant mismatch
              console.debug(`tenant-navigation: skipping item '${item.label}' path='${item.path ?? item.to ?? item.file ?? item.link?.path}' allowedTenants=${JSON.stringify(allowed)} userTenants=${JSON.stringify(userTenants)}`);
              // Skip item
              continue;
            }
            const copy = { ...item };
            if (copy.items) copy.items = filterNav(copy.items);
            // If a category became empty, skip it
            if (copy.type === "category" && copy.items && copy.items.length === 0 && !copy.collapsible) continue;
            result.push(copy);
          }
          return result;
        }

        const nav = site?.navigation ?? site;
        const filtered = filterNav(nav.items ?? nav);
        // Return a navigation copy with filtered items
        return Array.isArray(nav) ? filtered : { ...nav, items: filtered };
      } catch (err) {
        // swallow errors — do not break navigation rendering
        console.error("tenant-navigation plugin error", err);
      }
    },
  } as unknown as ZudokuPlugin;
}

export default createTenantNavigationPlugin;
